import React, { useState } from 'react';
import _ from 'lodash';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { CreateOrderBalance, CreateOrderField, CreatePercentSelectors, CreateOrderButton, ConfirmOrderModal } from 'containers/CreateOrder';
import { connect } from 'react-redux';

import { withNamespaces } from 'react-i18next';
import { nestedTranslate } from 'utils/strings';
import { PropTypes } from 'prop-types';
import { Form } from 'components/Form';
import { Select, Box, Text, Modal } from 'components/Wrapped';
import { getSingleTradingPairSettings } from 'redux/selectors/settings';
import { StatusInfo } from 'grommet-icons';

const STOPLIMIT = 'STOPLIMIT';
const STOPMARKET = 'STOPMARKET';

const stopOrderFormValidationSchema = (orderType, minTickSize, minTradeAmount, minOrderValue) => {
  switch (orderType) {
    case STOPLIMIT:
      return Yup.object().shape({
        rate: Yup.number()
          .required()
          .min(minTickSize),
        stop: Yup.number()
          .required()
          .min(minTickSize),
        volume: Yup.number()
          .required()
          .min(minTradeAmount),
        total: Yup.number().min(minOrderValue),
      });
    case STOPMARKET:
      return Yup.object().shape({
        stop: Yup.number()
          .required()
          .min(minTickSize),
        volume: Yup.number()
          .required()
          .min(minTradeAmount),
      });
    default:
      break;
  }
};

const StopOrderFormContainer = (props) => {
  const {
    side,
    tradingPair,
    placeOrder,
    orderType,
    t: translate,
    tradingPairSettings: { tickDecimals, tradeAmountDecimals, orderValueDecimals, minTickSize, minTradeAmount, minOrderValue },
  } = props;
  const additionalValues = {
    market: tradingPair.quoteCurrency,
    trade: tradingPair.baseCurrency,
    type: orderType,
  };
  const t = nestedTranslate(translate, 'forms.stopOrder');
  const [confirmModalVisibility, setConfirmModalVisibility] = useState(false);

  const isStopLimit = orderType === STOPLIMIT;

  const onSubmitForm = (values) => {
    const formValues = _.clone(values);

    if (additionalValues.type === STOPMARKET) {
      _.unset(formValues, 'rate');
      _.unset(formValues, 'trail');
      _.unset(formValues, 'isTrailInPercentage');
    } else {
      _.unset(formValues, 'trail');
      _.unset(formValues, 'isTrailInPercentage');
    }

    placeOrder({ ...formValues, ...additionalValues, side });
  };

  return (
    <Formik
      initialValues={{
        side,
        volume: '',
        rate: '',
        stop: '',
        trail: '',
        total: '',
        isTrailInPercentage: false,
      }}
      validationSchema={stopOrderFormValidationSchema(orderType, minTickSize, minTradeAmount, minOrderValue)}
      onSubmit={onSubmitForm}
    >
      {(props) => (
        <Form gap="small">
          <CreateOrderBalance tradingPair={tradingPair} side={side} />

          <CreateOrderField
            type="text"
            name="stop"
            label={t('stopPrice')}
            currency={tradingPair.quoteCurrency}
            formProps={props}
            side={side}
            precision={tickDecimals}
          />

          {isStopLimit && (
            <CreateOrderField
              type="text"
              name="rate"
              label={t('rate')}
              currency={tradingPair.quoteCurrency}
              formProps={props}
              side={side}
              precision={tickDecimals}
            />
          )}

          <CreateOrderField
            type="text"
            name="volume"
            label={t('volume')}
            currency={tradingPair.baseCurrency}
            formProps={props}
            side={side}
            precision={tradeAmountDecimals}
          />

          <CreatePercentSelectors side={side} total={props.values.total} amount={props.values.volume} />

          <CreateOrderField
            type="number"
            name="total"
            label={t('total')}
            currency={tradingPair.quoteCurrency}
            formProps={props}
            side={side}
            precision={orderValueDecimals}
            showErrors={true}
          />

          <CreateOrderButton
            side={side}
            tradingPair={tradingPair}
            onCreateOrderButtonClick={() => {
              props.validateForm().then((errors) => {
                if (_.isEmpty(errors)) {
                  setConfirmModalVisibility(true);
                }
              });
            }}
          />

          <ConfirmOrderModal
            orderType="Стоп"
            tradeMode={side}
            price={props.values.rate}
            visible={confirmModalVisibility}
            baseCurrency={tradingPair.baseCurrency}
            quoteCurrency={tradingPair.quoteCurrency}
            amount={props.values.volume}
            onToggleModal={() => {
              setConfirmModalVisibility(!confirmModalVisibility);
            }}
            onСonfirmButtonClick={() => {
              props.submitForm().finally(() => {
                setConfirmModalVisibility(false);
              });
            }}
          />
        </Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state, props) => ({
  tradingPairSettings: getSingleTradingPairSettings(state, props),
});

const StopOrderForm = withNamespaces()(connect(mapStateToProps)(StopOrderFormContainer));

export const TradingRules = withNamespaces()(({ t: translate }) => {
  return (
    <React.Fragment>
      <Box pad="small">
        <Box pad="none" margin={{ bottom: 'small' }}>
          <Text size="small" margin={{ bottom: 'xsmall' }}>{`${translate('exchange.BUY')} ${translate(
            'forms.stopOrder.stopLimit',
          )} & ${translate('forms.stopOrder.stopMarket')}`}</Text>
          <Text size="xsmall">{`- ${translate('tables.tradeHistory.stopprice')} > ${translate('forms.marketOrder.marketPrice')}`}</Text>
          <Text size="xsmall">{`- ${translate('tables.tradeHistory.stopprice')} < ${translate('forms.stopOrder.rate')} ${translate(
            'forms.limitOrder.rate',
          )}`}</Text>
        </Box>
        <Box pad="none" margin={{ bottom: 'small' }}>
          <Text size="small" margin={{ bottom: 'xsmall' }}>{`${translate('exchange.SELL')} ${translate(
            'forms.stopOrder.stopLimit',
          )} & ${translate('forms.stopOrder.stopMarket')}`}</Text>
          <Text size="xsmall">{`- ${translate('tables.tradeHistory.stopprice')} < ${translate('forms.marketOrder.marketPrice')}`}</Text>
          <Text size="xsmall">{`- ${translate('tables.tradeHistory.stopprice')} > ${translate('forms.stopOrder.rate')} ${translate(
            'forms.limitOrder.rate',
          )}`}</Text>
        </Box>
      </Box>
    </React.Fragment>
  );
});

export const TradingRulesButton = withNamespaces()(({ t: translate }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(!isOpen);

  return (
    <React.Fragment>
      <Box align="center" justify="center" background="#C55445" height={'32px'} width={'32px'} pad="none" onClick={() => toggleModal()}>
        <StatusInfo size="16px" />
      </Box>
      <Modal show={isOpen} toggleModal={toggleModal} pad="small" width="medium">
        <TradingRules handleSuccess={toggleModal} />
      </Modal>
    </React.Fragment>
  );
});

class StopOrder extends React.Component {
  state = {
    orderType: {
      value: 'STOPLIMIT',
      placeholder: '',
    },
  };

  static propTypes = {
    tradingPair: PropTypes.object.isRequired,
    placeOrder: PropTypes.func.isRequired,
  };

  handleChange = (orderType) => {
    this.setState({ orderType });
  };

  renderOptions() {
    const { t: translate } = this.props;
    const t = nestedTranslate(translate, 'forms.stopOrder');

    return [
      { value: STOPLIMIT, label: t('stopLimit') },
      { value: STOPMARKET, label: t('stopMarket') },
      // { value: TRAILINGSTOPMARKET, label: t('trailingStop') },
    ];
  }

  componentDidMount() {
    this.setState({ orderType: this.renderOptions()[0] });
  }

  render() {
    const { placeOrder, tradingPair, tradeMode } = this.props;

    return (
      <Box margin={{ top: 'small' }} pad="none">
        <Box flex={false} pad="none">
          <Box pad="none" direction="row" gap="10px" align="center">
            <Box pad="none" fill="horizontal">
              <Select
                options={this.renderOptions()}
                onChange={this.handleChange}
                defaultValue={this.state.orderType}
                valueKey="value"
                labelKey="label"
                valueSize="xsmall"
                background="new-page-background-2"
                borderColor="new-page-background-2"
                margin="none"
              />
            </Box>
            <TradingRulesButton />
          </Box>
        </Box>
        <Box pad={{ vertical: '12px', horizontal: '0px' }}>
          <StopOrderForm
            tradingPair={tradingPair}
            orderType={this.state.orderType.value}
            side={tradeMode === 'buy' ? 'BUY' : 'SELL'}
            placeOrder={placeOrder}
          />
        </Box>
      </Box>
    );
  }
}

export default withNamespaces()(StopOrder);
